/**
 * Defect status / assignment / lifecycle mutations (TES-1646)
 *
 * Extracts the inline `supabase` read/write calls that previously lived in
 * src/pages/Defects.tsx (status change, quick-assign, reopen, soft-delete,
 * bulk-reassign) into a hook so the page no longer bypasses the hook layer.
 *
 * Each write performs ONLY the data operation (error → throw), exactly as the
 * inline page code did. Cache invalidation is the shared `invalidateDefects`
 * method — callers invoke it on success exactly where they did before,
 * preserving ordering. The caller keeps the i18n toasts and notification
 * side-effects, which are UI concerns — behavior is unchanged.
 *
 * NB: these writes intentionally do NOT request `{ count: "exact" }` / inspect
 * the row count — the original page handlers didn't, and this is a behavior-
 * preserving lift. Silent-RLS-no-op detection lives in the heavier
 * useUpdateDefectMutation (useDefectMutations.ts), not here.
 *
 * The heavier create/update flow with offline support lives in useDefectMutations.ts.
 */

import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DefectStatus } from "@/types";
import { defectsKeys } from "./useDefectsQuery";
import { invalidateDefectCounts } from "@/lib/invalidateOpenCounts";
import { connectivityService } from "@/lib/connectivityService";

/** Subset of defect columns fetched for notification routing on a status change. */
interface DefectNotificationTarget {
  title: string;
  assigned_to: string | null;
  created_by: string | null;
  verifying_qm: string | null;
}

export function useDefectStatusMutations(tenantId: string | null, userId: string | undefined) {
  const queryClient = useQueryClient();

  /**
   * Invalidate the defect lists + open-count badges after a mutation.
   * Offline mode skips invalidation — the offline queue + local merging drive
   * the UI update there (mirrors the previous in-page `invalidateDefects`).
   */
  const invalidateDefects = () => {
    if (connectivityService.isOnline) {
      queryClient.invalidateQueries({ queryKey: defectsKeys.all });
      invalidateDefectCounts(queryClient, tenantId, userId);
    }
  };

  /**
   * Set a defect's status (used by start-progress and the generic status change).
   *
   * TES-2229 (LH §L5.10, Mig 399): dieser Plain-UPDATE deckt NUR noch die freien Bahnen der
   * serverseitigen Status-Maschine ab (open→in_progress, in_progress⇄resolved).
   * `cancelled` läuft über CancelDefectDialog→rpc_cancel_defect, `closed` über
   * VerifyDefectDialog→verify_defect, Reopen über reopen() (rpc_reopen_defect) —
   * alles andere weist der DB-Guard guard_defect_status_transition ab (P0409).
   */
  const updateStatus = async (defectId: string, status: DefectStatus): Promise<void> => {
    const { error } = await supabase
      .from("defects")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", defectId);
    if (error) throw error;
  };

  /** Fetch the columns needed to route status-change notifications. */
  const fetchNotificationTarget = async (
    defectId: string,
  ): Promise<DefectNotificationTarget | null> => {
    const { data } = await supabase
      .from("defects")
      .select("title, assigned_to, created_by, verifying_qm")
      .eq("id", defectId)
      .single();
    return data ?? null;
  };

  /** Assign (or unassign, when userId is null) a single defect. */
  const assign = async (defectId: string, assigneeId: string | null): Promise<void> => {
    const { error } = await supabase
      .from("defects")
      .update({ assigned_to: assigneeId, updated_at: new Date().toISOString() })
      .eq("id", defectId);
    if (error) throw error;
  };

  /**
   * Reopen a closed/cancelled defect („Storno aufheben") — server-gated via rpc_reopen_defect
   * (TES-2229, LH §L5.10: NUR Ersteller oder zuständiger Verifizierer/Deputy, auditiert).
   * Der RPC setzt Resolutions-/Storno-Metadaten zurück (wie der frühere Plain-UPDATE, der
   * jetzt vom Status-Guard abgewiesen würde).
   */
  const reopen = async (defectId: string, reason?: string): Promise<void> => {
    const { error } = await (
      supabase as unknown as {
        rpc: (
          fn: "rpc_reopen_defect",
          args: { _defect_id: string; _reason: string | null },
        ) => Promise<{ error: { code?: string; message: string } | null }>;
      }
    ).rpc("rpc_reopen_defect", { _defect_id: defectId, _reason: reason?.trim() || null });
    if (error) throw error;
  };

  /** Soft-delete a defect by stamping deleted_at (moves it to trash). */
  const softDelete = async (defectId: string): Promise<void> => {
    const { error } = await supabase
      .from("defects")
      .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq("id", defectId);
    if (error) throw error;
  };

  /** Bulk-reassign every defect in `defectIds` to `assigneeId`. */
  const bulkReassign = async (defectIds: string[], assigneeId: string): Promise<void> => {
    const { error } = await supabase
      .from("defects")
      .update({ assigned_to: assigneeId, updated_at: new Date().toISOString() })
      .in("id", defectIds);
    if (error) throw error;
  };

  return {
    invalidateDefects,
    updateStatus,
    fetchNotificationTarget,
    assign,
    reopen,
    softDelete,
    bulkReassign,
  };
}
